import css from 'styled-jsx/css';
import { colors, icons, typography, zIndex } from '../../../config/styles';
import { svgToDataUri } from '../../../utils/styles/svgMixins';

const DRAWER_WIDTH = 760;
const SIZE_CHART_DRAWER_WIDTH = 466;
const DrawerStyles = css`
  .drawer-container {
    width: 100%;
    position: fixed;
    background-color: ${colors.white.shade1};
    right: 0;
    top: 0;
    z-index: ${zIndex.drawer.container};
    height: 100%;
    transform: translateX(100%);
    transition: transform 0.2s ease;
    @mixin tabletToDesktop {
      transform: translateX(${DRAWER_WIDTH}px);
      width: ${DRAWER_WIDTH}px;
    }
  }

  .drawer-container.size-chart-drawer {
    @mixin tabletToDesktop {
      transform: translateX(${SIZE_CHART_DRAWER_WIDTH}px);
      width: ${SIZE_CHART_DRAWER_WIDTH}px;
      border-radius: 20px 0 0 20px;
    }
  }

  .drawer-container.is-open {
    transform: translateX(0);
  }

  .drawer-container.secondary {
    transform: translateY(100%);
    border-radius: 15px 15px 0 0;
    overflow: hidden;
  }

  .drawer-container.secondary.is-open {
    transform: translateY(80px);
  }

  .drawer-layout {
    display: block;
    position: absolute;
    left: 0;
    top: 0;
    min-height: 100%;
    width: 100%;
  }

  .drawer-content {
    width: 100%;
  }

  .drawer-content.secondary {
    padding-bottom: 30%;
  }

  .drawer-container :global(iframe) {
    height: 90vh;
    width: 100%;
  }

  .drawer-container :global(.doc-click-overlay) {
    height: 100%;
    width: 100%;
    align-items: flex-start;
    flex-direction: column;
    @mixin phabletToDesktop {
      position: inherit;
      align-items: inherit;
      justify-content: inherit;
    }
  }

  .warranty-icon {
    display: flex;
    height: 24px;
    min-width: 24px;
    background-image: url('${svgToDataUri(icons.warrantyIcon)}');
    background-size: 100%;
    background-position: center;
  }

  .services-icon {
    display: flex;
    height: 24px;
    min-width: 24px;
    background-image: url('${svgToDataUri(icons.servicesIcon)}');
    background-size: 100%;
    background-position: center;
  }
`;

const DrawerGlobalStyles = css.global`
  body.drawer-active {
    overflow: hidden;
    width: 100%;
  }

  body.drawer-active .background-overlay {
    opacity: 1;
    background-color: rgba(0, 0, 0, 0.5);
  }

  body.drawer-active .loader {
    width: 100%;
    left: auto;
    @mixin tabletToDesktop {
      width: ${DRAWER_WIDTH}px;
    }
  }

  body.drawer-active .size-chart-drawer .loader {
    @mixin tabletToDesktop {
      width: ${SIZE_CHART_DRAWER_WIDTH}px;
    }
  }

  .drawer-header {
    background-color: ${colors.black.shade1};
    font-size: ${typography.lg2};
    color: ${colors.white.shade1};
    padding: 27px 0;
    text-align: center;
    font-weight: bold;
    position: relative;
    display: block;
    height: 68px;
    width: 100%;
    font-family: Roboto, sans-serif;
    font-style: normal;
    letter-spacing: -0.03em;
    line-height: 20px;
  }

  .drawer-header i {
    position: absolute;
    right: 25px;
    cursor: pointer;
  }

  .drawer-header .title {
    display: flex;
    gap: 8px;
  }

  .drawer-header.secondary {
    background: none;
    color: ${colors.black.shade12};
    text-align: left;
    letter-spacing: 0;
    font-family: inherit;
    font-size: 18px;
    font-weight: normal;
    line-height: 24px;
    padding: 12px 20px 12px 20px;
    height: fit-content;
    position: sticky;
    top: 0;
    background-color: ${colors.white.shade1};
    z-index: 1;
  }

  .drawer-header.secondary .divider {
    border-bottom: 1px solid ${colors.gray.shade2};
  }

  .drawer-header.secondary i {
    position: absolute;
    right: 12px;
    top: 14px;
    cursor: pointer;
    font-size: 16px;
  }

  .drawer-header.secondary .drawer-line {
    width: 40px;
    height: 5px;
    border-radius: 5px;
    background: #a9b5c0;
    display: flex;
    align-self: center;
    margin: 0 auto 19px auto;
  }

  .drawer-header.secondary .sub-heading {
    padding-top: 12px;
    color: ${colors.gray.shade45};
    font-size: 1.8rem;
    font-weight: 300;
    line-height: 21.6px;
    letter-spacing: -0.3px;
  }

  .drawer-container.auto-height {
    height: auto;
    max-height: 80vh;
    bottom: 0;
    top: auto;
    transform: translateY(100%);
  }

  .drawer-container.auto-height.is-open {
    transform: translateY(0);
  }

  .drawer-container.secondary.auto-height {
    height: auto;
    max-height: 80vh;
    bottom: 0;
    top: auto;
    transform: translateY(100%);
    border-radius: 15px 15px 0 0;
  }

  .drawer-container.secondary.auto-height.is-open {
    transform: translateY(0);
  }

  .drawer-container.secondary.auto-height.size-chart-drawer {
    height: auto;
    max-height: 95vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    transition: max-height 0.3s ease-in-out;
  }

  .drawer-container.auto-height .drawer-layout {
    position: relative;
    min-height: 95vh;
    height: auto;
  }

  .drawer-container.auto-height .drawer-content.secondary {
    padding-bottom: 10px;
  }

  .drawer-container.secondary.auto-height.size-chart-drawer .drawer-layout {
    display: flex;
    flex-direction: column;
    max-height: 100%;
    overflow: hidden;
  }

  .drawer-container.secondary.auto-height.size-chart-drawer :global(.doc-click-overlay) {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }

  .drawer-container.secondary.auto-height.size-chart-drawer .drawer-content.secondary {
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    flex: 1;
    min-height: 0;
    padding-bottom: 20px;
  }

  .drawer-header.size-chart-header {
    color: ${colors.black.shade1};
    padding: 20px;
    height: auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-shrink: 0;
    width: 100%;
    background-color: ${colors.white.shade1};
    @mixin tabletToDesktop {
      border-radius: 20px 0 0 0;
    }
  }

  .drawer-container.secondary .drawer-header.size-chart-header {
    position: sticky;
    top: 0;
    z-index: 10;
    padding-top: 30px;
  }

  .drawer-container.secondary .drawer-header.size-chart-header::before {
    content: '';
    width: 40px;
    height: 5px;
    border-radius: 5px;
    background: #a9b5c0;
    position: absolute;
    left: 50%;
    top: 12px;
    transform: translateX(-50%);
  }

  .drawer-header.size-chart-header .size-chart-title {
    font-family: 'Lato', sans-serif;
    font-size: 18px;
    font-weight: 600;
    color: ${colors.black.shade1};
    letter-spacing: normal;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .drawer-header.size-chart-header .close-button {
    position: static;
    cursor: pointer;
    font-size: 20px;
    color: ${colors.black.shade1};
  }

  .drawer-header.size-chart-header i {
    position: static;
  }

  .ruler-icon {
    display: inline-block;
    width: 20px;
    height: 20px;
    background-image: url('${svgToDataUri(icons.rulerIcon)}');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
  }
`;

export { DrawerGlobalStyles, DrawerStyles };
